import { Controller } from '@hotwired/stimulus';
/**
 * Handles video download status for WLA.
 * Can be used on any element.
 * Can be used multiple times in the document.
 *
 * Targets
 * -------
 *
 * data                       - (required) - data container
 * download                   - (required) - download button
 * downloaded                 - (required) - downloaded button
 * downloadingWithProgress    - (required) - downloading with progress button
 * downloadingWithoutProgress - (required) - downloading without progress button
 * progress                   - (required) - progress bar
 *
 * Values
 * -------
 *
 * - id - (required) - post ID
 *
 * Usage
 * -------
 *
 * <div
 *   data-controller="wla-download"
 *   data-action="wlaDownloadsStatus@document->wla-download#handleEvent"
 *   data-wla-download-id-value="2"
 * >
 *   <div data-wla-download-target="data">{ ... }</div>
 *
 *   <button
 *     data-wla-download-target="download"
 *     data-action="click->wla-download#handleDownloadClick"
 *   >
 *     Download
 *   </button>
 *
 *   <button
 *     data-wla-download-target="downloaded"
 *     data-action="click->wla-download#handleDownloadedClick"
 *   >
 *     Downloaded
 *   </button>
 *
 *   <button
 *     data-wla-download-target="downloadingWithoutProgress"
 *     data-action="click->wla-download#handleDownloadingClick"
 *   >
 *     Downloading
 *   </button>
 *
 *   <button
 *     data-wla-download-target="downloadingWithProgress"
 *     data-action="click->wla-download#handleDownloadingClick"
 *   >
 *     <div data-wla-download-target="progress"></div>
 *     Downloading
 *   </button>
 * </div>
 */
const LOCAL_STOREAGE_KEY = 'wla_download_list';
export default class default_1 extends Controller {
    static targets = [
        'data',
        'download',
        'downloaded',
        'downloadingWithProgress',
        'downloadingWithoutProgress',
        'progress',
    ];
    static values = { id: Number };
    connect() {
        const data = JSON.parse(localStorage.getItem(LOCAL_STOREAGE_KEY) || '[]');
        if (data)
            this.showStatus(data);
    }
    handleEvent(e) {
        localStorage.setItem(LOCAL_STOREAGE_KEY, JSON.stringify(e.detail));
        this.showStatus(e.detail);
    }
    showStatus(data) {
        this.downloadTarget.disabled = false;
        const postStatus = data.find((status) => status.postId === this.idValue);
        if (!postStatus)
            return this.showDownload();
        if (postStatus.status === 'downloaded')
            return this.showDownloaded();
        if (postStatus.status === 'downloading') {
            if (postStatus.progress === -1)
                return this.showDownloadingWithoutProgress();
            this.setProgress(postStatus.progress * 100);
            return this.showDownloadingWithProgress();
        }
    }
    handleDownloadClick() {
        window.webkit?.messageHandlers?.storeOffline?.postMessage?.(JSON.parse(this.mediaData));
        window.Android?.storeOffline?.(this.mediaData);
    }
    handleDownloadingClick() {
        window.webkit?.messageHandlers?.manageDownloadingOffline?.postMessage?.(JSON.parse(this.mediaData));
        window.Android?.handleDownloadingButtonClick?.(this.mediaData);
    }
    handleDownloadedClick() {
        window.webkit?.messageHandlers?.manageDownloadedOffline?.postMessage?.(JSON.parse(this.mediaData));
        window.Android?.handleDownloadedButtonClick?.(this.mediaData);
    }
    showDownload() {
        this.downloadTarget.classList.remove('hidden');
        this.downloadedTarget.classList.add('hidden');
        this.downloadingWithProgressTarget.classList.add('hidden');
        this.downloadingWithoutProgressTarget.classList.add('hidden');
    }
    showDownloaded() {
        this.downloadTarget.classList.add('hidden');
        this.downloadedTarget.classList.remove('hidden');
        this.downloadingWithProgressTarget.classList.add('hidden');
        this.downloadingWithoutProgressTarget.classList.add('hidden');
    }
    showDownloadingWithProgress() {
        this.downloadTarget.classList.add('hidden');
        this.downloadedTarget.classList.add('hidden');
        this.downloadingWithProgressTarget.classList.remove('hidden');
        this.downloadingWithoutProgressTarget.classList.add('hidden');
    }
    showDownloadingWithoutProgress() {
        this.downloadTarget.classList.add('hidden');
        this.downloadedTarget.classList.add('hidden');
        this.downloadingWithProgressTarget.classList.add('hidden');
        this.downloadingWithoutProgressTarget.classList.remove('hidden');
    }
    setProgress(progress) {
        this.progressTarget.style.setProperty('--progress', progress + '%');
    }
    get mediaData() {
        return this.dataTarget.innerHTML;
    }
}
